import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Clock, Check, Cpu, HeartHandshake, ArrowRight, Phone, ArrowUpRight } from "lucide-react";
import { Container, Button, Eyebrow, SectionHeading } from "@/components/primitives";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { CTASection } from "@/components/sections";
import { useBooking } from "@/lib/booking";
import { useSeo } from "@/lib/seo";
import { services, business } from "@/data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  const { openBooking } = useBooking();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);

  useSeo({
    title: service?.seoTitle,
    description: service?.seoDescription,
    path: `/services/${slug}`,
  });

  if (!service) return <Navigate to="/services" replace />;

  const related = service.related.map((r) => services.find((s) => s.slug === r)).filter(Boolean);

  return (
    <>
      <section ref={ref} className="relative overflow-hidden pt-32 sm:pt-40">
        <Container>
          <div className="grid items-end gap-10 pb-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <Reveal><Eyebrow>Service {service.index}</Eyebrow></Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-5 font-heading text-5xl font-light leading-[0.98] tracking-tight text-[var(--fg)] sm:text-6xl md:text-7xl text-balance">
                  {service.name}
                </h1>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-7 max-w-xl text-base font-light leading-relaxed text-[var(--muted-fg)] sm:text-lg">{service.intro}</p>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <Button size="lg" onClick={() => openBooking(service.name)} data-testid="service-book-btn">
                    Book Appointment <ArrowRight size={16} />
                  </Button>
                  <Button size="lg" variant="outline" href={business.phoneHref} data-testid="service-call-btn">
                    <Phone size={16} /> Call / Text for Inquiries
                  </Button>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-[var(--border)]">
                <motion.img style={{ y }} src={service.hero} alt={`${service.name} at Meridian Dental Studio, Hell's Kitchen`} className="absolute inset-0 h-[115%] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Treatments */}
      <section className="border-t border-[var(--border)] py-24 sm:py-32">
        <Container>
          <SectionHeading eyebrow="Treatment overview" title="What's included & what to expect." />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2">
            {service.treatments.map((t) => (
              <StaggerItem key={t.name}>
                <div className="flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-7">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-2xl font-normal tracking-tight text-[var(--fg)]">{t.name}</h3>
                    <span className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.12em] text-[var(--primary)]">
                      <Clock size={13} /> {t.duration}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--muted-fg)]">{t.note}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </section>

      {/* Technology + Comfort */}
      <section className="bg-[var(--surface)] py-24 sm:py-32">
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <Reveal>
              <div className="flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-8 sm:p-10">
                <Cpu size={26} className="text-[var(--primary)]" />
                <h3 className="mt-5 font-heading text-3xl font-light tracking-tight text-[var(--fg)]">Advanced technology</h3>
                <ul className="mt-6 space-y-3">
                  {service.technology.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-[var(--fg)]/85">
                      <Check size={16} className="mt-0.5 shrink-0 text-[var(--primary)]" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-8 sm:p-10">
                <HeartHandshake size={26} className="text-[var(--primary)]" />
                <h3 className="mt-5 font-heading text-3xl font-light tracking-tight text-[var(--fg)]">Comfort & amenities</h3>
                <ul className="mt-6 space-y-3">
                  {service.comfort.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-[var(--fg)]/85">
                      <Check size={16} className="mt-0.5 shrink-0 text-[var(--primary)]" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Why choose us */}
      <section className="py-24 sm:py-32">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <SectionHeading eyebrow="Why Meridian" title="Specialists who lead their field." />
            <div className="space-y-6">
              <p className="text-lg font-light leading-relaxed text-[var(--fg)]/85">
                Our clinicians train at institutions like NYU and Columbia and pursue advanced credentials in cosmetic, implant and orthodontic dentistry. But credentials are only half of it — every plan is built around you, explained in plain language, and priced transparently before you commit.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button to="/about" variant="outline" data-testid="service-meet-doctors">Meet the Doctors</Button>
                <Button to="/insurance-financing" variant="ghost">View Insurance & Financing <ArrowRight size={15} /></Button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Related */}
      <section className="bg-[var(--surface)] py-24 sm:py-32">
        <Container>
          <SectionHeading eyebrow="Keep exploring" title="Related treatments." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {related.map((r) => (
              <Reveal key={r.slug}>
                <Link to={`/services/${r.slug}`} data-testid={`related-${r.slug}`} className="group flex items-center justify-between gap-4 rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-8 transition-colors hover:border-[var(--primary)]">
                  <div>
                    <span className="font-mono text-xs text-[var(--primary)]">{r.index}</span>
                    <h3 className="mt-2 font-heading text-2xl font-normal tracking-tight text-[var(--fg)]">{r.name}</h3>
                    <p className="mt-2 max-w-sm text-sm text-[var(--muted-fg)]">{r.short}</p>
                  </div>
                  <ArrowUpRight size={24} className="shrink-0 text-[var(--muted-fg)] transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[var(--primary)]" />
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <CTASection eyebrow={`Book ${service.name}`} title="Let's get you on the schedule." intro={`Same-week ${service.name.toLowerCase()} appointments in Hell's Kitchen. Book online or call/text us today.`} />
    </>
  );
}
